import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

export type InvoicePreviewLine = {
  description: string;
  amount: number;
  quantity: number;
  proration: boolean;
  periodStart?: { seconds?: number } | null;
  periodEnd?: { seconds?: number } | null;
  priceId: string;
  source: string;
};

export type InvoicePreview = {
  dueNow: number;
  subtotal: number;
  discount: number;
  appliedBalance: number;
  startingBalance: number;
  currency: string;
  linesList: InvoicePreviewLine[];
  nextInvoiceAmount: number;
  nextInvoiceDate?: { seconds?: number } | null;
  /** The discount is the customer's waiting cancel offer: billing-manager puts it on new plans only. */
  cancelOfferApplied?: boolean;
  /** The waiting cancel offer's percent off, in basis points; 0 when none waits. */
  cancelOfferPercentBps?: number;
} | null;

interface InvoicePreviewState {
  preview: InvoicePreview;
  loading: boolean;
  error: unknown;
  /**
   * Whether the currently resolved plan contains at least one purchase op —
   * a Create (brand-new subscription), an AddItem, or a Modify on an existing
   * one. A discount code rides every one of them (creates apply it at
   * subscription level, adds/modifies at item level on the changed line), so
   * this gates the discount-code UI. Published by `useInvoicePreviewSync`
   * each time the plan is re-resolved, so it tracks cart changes live.
   */
  hasPurchaseOps: boolean;
  /**
   * The discount code the settled `preview` was QUOTED with ('' when none).
   * Published atomically with the preview so consumers can tell a quote that
   * reflects the currently-applied code from a stale pre-apply quote — the
   * source of the "couldn't apply that code" flash right after Apply.
   */
  quotedCoupon: string;
  /** The settled `preview` was quoted for a crypto payment: no cancel offer, added lines as their own plans. */
  quotedForCrypto: boolean;
}

interface InvoicePreviewActions {
  setPreview: (preview: InvoicePreview, quotedCoupon: string, quotedForCrypto?: boolean) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: unknown) => void;
  setHasPurchaseOps: (hasPurchaseOps: boolean) => void;
  reset: () => void;
}

const INITIAL_STATE: InvoicePreviewState = {
  preview: null,
  loading: false,
  error: null,
  hasPurchaseOps: false,
  quotedCoupon: '',
  quotedForCrypto: false
};

export const useInvoicePreviewStore = create<InvoicePreviewState & InvoicePreviewActions>()((set) => ({
  ...INITIAL_STATE,
  setPreview: (preview, quotedCoupon, quotedForCrypto = false) => set({ preview, quotedCoupon, quotedForCrypto }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  setHasPurchaseOps: (hasPurchaseOps) => set({ hasPurchaseOps }),
  reset: () => set(INITIAL_STATE)
}));

/**
 * Public read hook. Returns the same `{preview, loading, error}` shape the old
 * React-Context-based `useInvoicePreview` returned — call from any component
 * inside the checkout drawer; re-renders only when one of those three fields
 * changes (shallow compare).
 */
export function useInvoicePreview(): Pick<InvoicePreviewState, 'preview' | 'loading' | 'error' | 'quotedCoupon' | 'quotedForCrypto'> {
  return useInvoicePreviewStore(
    useShallow((s) => ({
      preview: s.preview,
      loading: s.loading,
      error: s.error,
      quotedCoupon: s.quotedCoupon,
      quotedForCrypto: s.quotedForCrypto
    }))
  );
}

/**
 * Whether the resolved plan contains at least one purchase op (create, add or
 * modify) — every one of them can carry a discount code.
 */
export function usePlanHasPurchaseOps(): boolean {
  return useInvoicePreviewStore((s) => s.hasPurchaseOps);
}
